import React, { createContext, useState, useMemo } from "react";
import { View, ScrollView } from "react-native";

import { useQaLabel } from "../../hooks/useQaLabel";
import { TabsProps, TabsContextProps, TabsLayout } from "./types";
import {
  StyledViewContainer,
  ContentView,
  TabHeaderContainer
} from "./styled-components";

const initialState: TabsContextProps = {
  selectedValue: "",
  onChange: () => {},
  isStretched: false,
  tabsLayout: {},
  setTabsLayout: () => {},
  isFullHeight: false
};

export const TabsContext = createContext<TabsContextProps>(initialState);
TabsContext.displayName = "TabsContext";

export const Tabs = ({
  qaLabel,
  children,
  selectedValue,
  onChange,
  isFullHeight = false,
  useViewComponent = false,
  headerPadding,
  headerWidth,
  ...rest
}: TabsProps) => {
  const content = useMemo(() => {
    const childrenElements = React.Children.toArray(children);
    return childrenElements.filter((val) => {
      if (React.isValidElement<{ value: string }>(val)) {
        return val.props.value === selectedValue;
      }
      return val;
    });
  }, [selectedValue, children]);

  const viewTestProps = useQaLabel(qaLabel);
  const [tabsLayout, setTabsLayout] = useState<TabsLayout>({});

  let tabList;

  if (children[0]?.props.qaLabel === "tabList") {
    tabList = children[0];
  } else {
    throw new Error(
      "TabList child element is required to be used at first place"
    );
  }

  return (
    <StyledViewContainer
      isFullHeight={isFullHeight}
      {...rest}
      {...viewTestProps}
    >
      <TabsContext.Provider
        value={{
          selectedValue,
          onChange,
          tabsLayout,
          setTabsLayout,
          isFullHeight
        }}
      >
        <TabHeaderContainer
          headerPadding={headerPadding}
          headerWidth={headerWidth}
        >
          {tabList}
        </TabHeaderContainer>
        <ContentView
          as={useViewComponent ? View : ScrollView}
          isFullHeight={isFullHeight}
        >
          {content}
        </ContentView>
      </TabsContext.Provider>
    </StyledViewContainer>
  );
};

export { TabsProps };
